import ApprovalListViewModel from "../../common/approvalListViewModel";
import type AccountingDocumentCandidateResponse from "entities/accountingDocumentCandidateResponse";
import AccountingDocumentCandidateSearchRequest, {
  AccountingDocumentCandidateSearchRequestValidationRules,
} from "entities/accountingDocumentCandidateSearchRequest";
import type { ScreenBase } from "@frui.ts/screens";
import AccountingDocumentCandidateApprovalType from "entities/accountingDocumentCandidateApprovalType";
import LocalizationService from "services/localizationService";
import EventBus, { allApprovedAccountingDocumentsExported, approvedAccountingDetailsApproved } from "services/eventBus";
import SecurityService from "services/securityService";
import type CountrySelectViewModel from "../../countrySelectViewModel";
import type InternalTransferEditViewModel from "../internalTransfers/internalTransferEditViewModel";
import ConfirmationService from "services/confirmationService";
import NotificationService, { NotificationType } from "services/notificationService";
import { Router } from "@frui.ts/screens/dist";
import AccountingDocumentsApprovalService from "services/accountingDocumentsApprovalService";
import { computed } from "mobx";
import { Modules } from "../../../config";
import AccountingDocumentCandidatesRepository from "../../../repositories/accountingDocumentCandidatesRepository";
import { bound } from "@frui.ts/helpers";
import * as Sentry from "@sentry/react";
import type AccountingDocumentApprovalDetailViewModel from "./accountingDocumentApprovalDetailViewModel";
import type PagingManagerViewModel from "../../pagingManagerViewModel";
import MonthOptionsService from "../../../services/monthOptionsService";
import { attachAutomaticValidator } from "@frui.ts/validation/dist";
import OptionsService from "services/optionsService";
import AccountingTemplateResponse from "entities/accountingTemplateResponse";
import AccountingDocumentCandidateApprovalMethod from "../../../entities/accountingDocumentCandidateApprovalMethod";
import Selectable, * as selectableListViewModel from "../../selectableListViewModel";
import { emptyValue } from "utils/emptyValue";

const navigationName = "accounting-documents-approvals";

@Router.registerRoute({ route: navigationName, name: Router.Self })
@Selectable
export default class AccountingDocumentsApprovalsViewModel extends ApprovalListViewModel<
  AccountingDocumentCandidateApprovalType,
  AccountingDocumentCandidateResponse,
  ScreenBase,
  AccountingDocumentCandidateSearchRequest
> {
  readonly modelName = "accountingDocumentCandidate";
  readonly approvalType = AccountingDocumentCandidateApprovalType;

  @computed
  get name() {
    return this.translate("name");
  }

  @computed
  get self(): selectableListViewModel.SelectableListViewModel<
    AccountingDocumentsApprovalsViewModel,
    AccountingDocumentCandidateResponse
  > {
    return this as any;
  }

  @computed
  get approvalOptions() {
    return this.approvalService.approvalsOptions();
  }

  @computed
  get accountingPeriodOptions() {
    return this.monthOptionService.monthOptions;
  }

  @computed
  get canApproveAll() {
    return (
      !this.noCandidatesForApprove &&
      this.securityService.allowTo("finance", "write") &&
      (this.securityService.allowTo("finance.commandoperating", "write") ||
        this.securityService.allowTo("finance.chiefaccounting", "write"))
    );
  }

  @computed
  get canApproveSelected() {
    return this.self.selectedItems.size > 0;
  }

  @computed
  get noCandidatesForApprove() {
    return (
      !this.items.some(item => this.approvalService.canApprove(item)) &&
      this.currentPaging?.limit >= this.currentPaging?.totalItems
    );
  }

  @computed
  get accountingTemplatesOptions() {
    return this.optionsService.entityOptions(AccountingTemplateResponse);
  }

  constructor(
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    protected repository: AccountingDocumentCandidatesRepository,
    protected approvalService: AccountingDocumentsApprovalService,
    protected countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>,
    protected internalTransferEditViewModelFactory: ReturnType<typeof InternalTransferEditViewModel.Factory>,
    protected confirmationService: ConfirmationService,
    protected notificationService: NotificationService,
    protected detailViewModelFactory: ReturnType<typeof AccountingDocumentApprovalDetailViewModel.Factory>,
    protected pagingManagerViewModelFactory: ReturnType<typeof PagingManagerViewModel.Factory>,
    private monthOptionService: MonthOptionsService,
    private optionsService: OptionsService
  ) {
    super(localizationService, eventBus, approvalService, countrySelectViewModelFactory);
    this.navigationName = navigationName;
  }

  protected processData(items: AccountingDocumentCandidateResponse[]) {
    items.forEach(item => {
      item.isSelectable = !this.approvedByMe(item) && this.myApprovalStep(item);

      return item;
    });

    return items;
  }

  protected fetchData() {
    const { limit, offset } = this.pagingFilter;

    this.repository
      .getAll({ limit: 1, offset: 0 }, { approvedBy: AccountingDocumentCandidateApprovalType.ChiefAccountant })
      .then(response => {
        if (response.items.length > 0) {
          this.isAnyExportable = true;
        }
      });

    return this.repository.getAll({ offset, limit }, this.appliedFilter);
  }

  protected createFilter(): AccountingDocumentCandidateSearchRequest {
    return attachAutomaticValidator(
      new AccountingDocumentCandidateSearchRequest(),
      AccountingDocumentCandidateSearchRequestValidationRules
    );
  }

  @bound
  async onExportApprovedClick() {
    const confirmed = await this.confirmationService.showConfirmation(this.translate("confirmExportedDialog.title"), {
      confirmButton: { text: this.translate("confirmExportedDialog.confirm"), variant: "danger" },
    });

    if (!confirmed) {
      return;
    }

    try {
      await this.repository.export();
      this.notificationService.add({ title: this.translate("exported"), type: NotificationType.SUCCESS });
      this.eventBus.publish(allApprovedAccountingDocumentsExported());
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  @bound
  async onApproveAllClick() {
    const confirmed = await this.confirmationService.showConfirmation(this.translate("confirmAllApproval.title"), {
      confirmButton: { text: this.translate("confirmAllApproval.confirm"), variant: "danger" },
    });

    if (confirmed) {
      try {
        await this.repository.approvals({ approvalMethod: AccountingDocumentCandidateApprovalMethod.ApproveAll });
        this.notificationService.add({ title: this.translate("allApproved"), type: NotificationType.SUCCESS });
        this.eventBus.publish(approvedAccountingDetailsApproved());
      } catch (e) {
        // General error handle is good enough
      }
    }
  }

  @bound
  async onApproveSelectedClick() {
    const confirmed = await this.confirmationService.showConfirmation(this.translate("confirmSelectedApproval.title"), {
      confirmButton: { text: this.translate("confirmSelectedApproval.confirm"), variant: "danger" },
    });
    if (confirmed) {
      try {
        await this.repository.approvals({
          approvalMethod: AccountingDocumentCandidateApprovalMethod.ApproveSelected,
          accountingDocumentCandidateIds: Array.from(this.self.selectedItems).map(item => item.id),
        });
        this.self.selectedItems.clear();
        this.notificationService.add({ title: this.translate("selectedApproved"), type: NotificationType.SUCCESS });
        this.eventBus.publish(approvedAccountingDetailsApproved());
      } catch (e) {
        // General error handle is good enough
      }
    }
  }

  protected detailParams(itemId: any) {
    const pagingManager = this.pagingManagerViewModelFactory(this, itemId);
    return [pagingManager];
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `accountingDocumentsApprovals.${code}`, size);
  }

  translateFilterAttribute(attribute: keyof AccountingDocumentCandidateSearchRequest) {
    return this.localizationService.translateAttribute("accountingDocumentCandidateSearchRequest", attribute);
  }

  translateAccountingTemplateCode(accountingTemplateId: string) {
    return (
      this.optionsService.optionsViewModel(AccountingTemplateResponse).items.find(item => item.id === accountingTemplateId)
        ?.code ?? emptyValue
    );
  }
}
